/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// Mantine
import {
	Box,
	LoadingOverlay,
	Text,
	Tooltip,
} from '@mantine/core';

// Styles
import classes from './RowExpansion.module.css';

// I18n
import { useTranslation } from 'react-i18next';

// Constants
import { NOT_APPLICABLE } from '@config/constants/common';

// Utils
import { truncateString } from '@utils/functions';

// Apis
import { useOffsetById } from '@features/offsets/api/get-offset-by-id';

// Lodash
import isNil from 'lodash/isNil';

// Types
import { TParsedRowProject } from '@features/offsets/types';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
type RowExpansionProps = {
	offsetId: string;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const RowExpansion = (
	props: RowExpansionProps
) => {
	/* ******************************** Hooks ******************************* */
	const { t } = useTranslation();

	const { additionalities, measurabilities, isLoading } =
		useOffsetById(props.offsetId);

	const rowsAdditionality =
		Array.isArray(additionalities) &&
		additionalities.length > 0 ? (
			additionalities.map((item: TParsedRowProject) => (
				<Box key={item.id} className={classes.row}>
					<Tooltip
						disabled={
							(!isNil(item) && isNil(item.name)) ||
							(!isNil(item.name) &&
								item.name.length <= 30)
						}
						label={item.name}
					>
						<Box className={classes.divition}>
							<Text className={classes.rowText}>
								{truncateString(item.name, 30)}
							</Text>
						</Box>
					</Tooltip>
					<Box className={classes.divition}>
						<Text className={classes.rowText}>{''}</Text>
					</Box>
					<Box className={classes.divition}>
						<Text className={classes.rowText}>{''}</Text>
					</Box>
					<Box className={classes.divition}>
						<Text className={classes.rowText}>
							{item.total}
						</Text>
					</Box>
				</Box>
			))
		) : (
			<Box className={classes.row}>
				<Box className={classes.divition}>
					<Text className={classes.rowText}>
						{NOT_APPLICABLE}
					</Text>
				</Box>
				<Box className={classes.divition}>
					<Text className={classes.rowText}></Text>
				</Box>
				<Box className={classes.divition}>
					<Text className={classes.rowText}></Text>
				</Box>
				<Box className={classes.divition}>
					<Text className={classes.rowText}>
						{NOT_APPLICABLE}
					</Text>
				</Box>
			</Box>
		);

	const rowsMesurability =
		Array.isArray(measurabilities) &&
		measurabilities.length > 0 ? (
			measurabilities.map((item: TParsedRowProject) => (
				<Box key={item.id} className={classes.row}>
					<Tooltip
						disabled={
							(!isNil(item) && isNil(item.name)) ||
							(!isNil(item.name) &&
								item.name.length <= 30)
						}
						label={item.name}
					>
						<Box className={classes.divition}>
							<Text className={classes.rowText}>
								{truncateString(item.name, 30)}
							</Text>
						</Box>
					</Tooltip>
					<Box className={classes.divition}>
						<Text className={classes.rowText}>
							{item.price}
						</Text>
					</Box>
					<Box className={classes.divition}>
						<Text className={classes.rowText}>
							{item.quantity}
						</Text>
					</Box>
					<Box className={classes.divition}>
						<Text className={classes.rowText}>
							{item.total}
						</Text>
					</Box>
				</Box>
			))
		) : (
			<Box className={classes.row}>
				<Box className={classes.divition}>
					<Text className={classes.rowText}>
						{NOT_APPLICABLE}
					</Text>
				</Box>
				<Box className={classes.divition}>
					<Text className={classes.rowText}>
						{NOT_APPLICABLE}
					</Text>
				</Box>
				<Box className={classes.divition}>
					<Text className={classes.rowText}>
						{NOT_APPLICABLE}
					</Text>
				</Box>
				<Box className={classes.divition}>
					<Text className={classes.rowText}>
						{NOT_APPLICABLE}
					</Text>
				</Box>
			</Box>
		);

	/* ******************************** RENDERING ******************************* */
	return (
		<>
			{isLoading ? (
				<Box
					pos="relative"
					className={classes.containerOverlay}
				>
					<LoadingOverlay
						visible={true}
						zIndex={1000}
						overlayProps={{ radius: 'sm', blur: 2 }}
					/>
				</Box>
			) : (
				<Box className={classes.container}>
					<Box className={classes.header}>
						<Box className={classes.divition}>
							<Text className={classes.headerText}>
								{t('global.type')}
							</Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.headerText}>
								{t('global.project')}
							</Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.headerText}>
								{t('global.pricePerTco2e')}
							</Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.headerText}>
								{t('global.quantity')}
							</Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.headerText}>
								{t('global.totalPricePerProject')}
							</Text>
						</Box>
					</Box>

					<Box className={classes.row}>
						<Box className={classes.typeDivision}>
							<Text className={classes.typeText}>
								{t('global.measurabilitiesProjects')}
							</Text>
						</Box>
						{/*Mesurability projects rows */}
						<Box className={classes.rowsDivision}>
							{rowsMesurability}
						</Box>
					</Box>

					<Box className={classes.header}>
						<Box className={classes.divition}>
							<Text className={classes.headerText}>
								{t('global.type')}
							</Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.headerText}>
								{t('global.project')}
							</Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.headerText}></Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.headerText}></Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.headerText}>
								{t('global.totalAdditionality')}
							</Text>
						</Box>
					</Box>

					<Box className={classes.row}>
						<Box className={classes.typeDivision}>
							<Text className={classes.typeText}>
								{t('global.additionalProjects')}
							</Text>
						</Box>
						{/*Additional projects rows */}
						<Box className={classes.rowsDivision}>
							{rowsAdditionality}
						</Box>
					</Box>
				</Box>
			)}
		</>
	);
};
